import { HttpClient, HttpParams } from '@angular/common/http';
import { computed, inject, Injectable } from '@angular/core';
import { firstValueFrom, tap } from 'rxjs';
import { IamAction, IamActionValues } from 'src/app/models/permission';
import { environment } from 'src/environments/environment';
import { ApiCacheService } from '../cache-service/api-cache-service';
import { JwtService } from '../jwt/jwt-service';

@Injectable({
  providedIn: 'root'
})
export class PermissionService {
  private http = inject(HttpClient);
  private cache = inject(ApiCacheService);
  private jwtService = inject(JwtService);
  private baseURL: string = environment.HTTP;

  private basePermission = `Permission`;
  private apiGetSortedTemplateModules = `${this.baseURL}/${this.basePermission}/GetSortedTemplateModule`;
  private apiCreateTemplate = `${this.baseURL}/${this.basePermission}/CreateTemplate`;
  private apiUpdateTemplate = `${this.baseURL}/${this.basePermission}/UpdateTemplate`;
  private apiUpsertModuleTemplate = `${this.baseURL}/${this.basePermission}/UpsertModuleTemplate`;
  private apiGetSortedPermissionModules = `${this.baseURL}/${this.basePermission}/GetSortedPermissionModules`;
  private apiGetByRoleId = `${this.baseURL}/${this.basePermission}/GetByRoleId`;
  private apiUpsertPermission = `${this.baseURL}/${this.basePermission}/UpsertPermission`;
  private apiGetByUserId = `${this.baseURL}/${this.basePermission}/GetByUserId`;
  private apiUpsertReassignmentPermission = `${this.baseURL}/${this.basePermission}/UpsertUserReassignmentPermissions`;
  private apiUpdateFavourite = `${this.baseURL}/${this.basePermission}/UpdateFavourite`;
  private apiGetPermissionByUserCode = `${this.baseURL}/${this.basePermission}/GetPermissionByUserCode`;

  //check if its global/country - only certain access allowed for country
  // isCountry: boolean = this.jwtService.getCountry() !== 'GLOBAL';
  isCountry = computed(() => this.jwtService.getCountry() !== 'GLOBAL');
  countryAllowedPermissions: IamAction[] = [IamAction.READ, IamAction.EXPORT];
  countryAllowedCRUDModules: string[] = ['proc_pr', 'proc_local_po', 'proc_asn', 'proc_grn', 'proc_pro', 'proc_pcn', 'proc_pi'];

  getUserCode() {
    const user = localStorage.getItem('user');
    if (!user) return null;

    return JSON.parse(user).userCode;
  }

  getSortedTemplateModules(departmentId: number | null, isDefault: boolean) {
    let params = new HttpParams();
    if (departmentId != null) {
      params = params.set('departmentId', departmentId);
    }
    params = params.set('isDefault', isDefault);

    return this.http.get(this.apiGetSortedTemplateModules, { params: params });
  }

  createTemplate(data: any) {
    return this.http.post(this.apiCreateTemplate, data);
  }

  updateTemplate(data: any) {
    return this.http.post(this.apiUpdateTemplate, data);
  }

  upsertModuleTemplate(data: any) {
    return this.http.post(this.apiUpsertModuleTemplate, data);
  }

  getSortedPermissionModules(departmentId?: string, roleId?: number) {
    let params = new HttpParams();
    if (departmentId != null) {
      params = params.set('departmentId', departmentId);
    }
    if (roleId != null) {
      params = params.set('roleId', roleId);
    }

    return this.http.get(this.apiGetSortedPermissionModules, { params: params });
  }

  getByRoleId(roleId: number) {
    return this.http.get(this.apiGetByRoleId, { params: { roleId: roleId } });
  }

  upsertPermission(data: any) {
    return this.http.post(this.apiUpsertPermission, data);
  }

  getByUserId(userId: number, reassignmentId: number) {
    return this.http.get(this.apiGetByUserId, { params: { userId: userId, reassignmentId: reassignmentId } });
  }

  upsertReassignmentPermission(data: any, uniqueIdentifier?: string, requestStatus?: 'DRAFT' | 'SUBMITTED') {
    let params = new HttpParams();
    if (uniqueIdentifier) {
      params = params.set('uniqueIdentifier', uniqueIdentifier);
    }
    if (requestStatus) {
      params = params.set('requestStatus', requestStatus);
    }
    return this.http.post(this.apiUpsertReassignmentPermission, data, { params });
  }

  UpdateFavourite(id: any) {
    return this.http.post(this.apiUpdateFavourite, id).pipe(
      tap(() => {
        this.cache.clear();
      })
    );
  }

  getPermissionsByUserCode() {
    const userCode = this.getUserCode();
    return this.http.get(this.apiGetPermissionByUserCode, { params: { userCode: userCode } });
  }

  //LAYERS
  // ROOT (NULL) -> MAIN_MODULE -> CATEGORY -> SUB_MODULE / APPROVAL_SUB_MODULE -> ACTIONS
  flattenModules(node: any): any[] {
    const modules: any[] = [];

    if (node.moduleType === 'SUB_MODULE' || node.moduleType === 'APPROVAL_SUB_MODULE') {
      modules.push(node);
    }

    for (const child of node.submodules ?? []) {
      modules.push(...this.flattenModules(child));
    }

    return modules;
  }

  //#region Permissions
  userPermsCached: any[] = [];
  async setUserPermsCache() {
    if (this.userPermsCached.length > 0) return;

    try {
      const result: any = await firstValueFrom(this.getPermissionsByUserCode());
      this.userPermsCached = JSON.parse(result.data).menuList;
    } catch (error) {
      console.error('Failed to fetch permissions', error);
      this.userPermsCached = [];
    }
  }

  clearUserPermsCache() {
    this.userPermsCached = [];
  }

  async hasPermission(moduleCode: string, checkPerm: IamAction) {
    if (!moduleCode) return false;
    if (this.userPermsCached.length === 0) await this.setUserPermsCache();

    const userModulePerms = this.userPermsCached.find((item) => item.moduleCode === moduleCode);
    if (!userModulePerms) return false;

    const userModuleAction = userModulePerms.action;
    const hasPermission = (userModuleAction & checkPerm) === checkPerm;

    if (!hasPermission) return false;

    //check if its global/country - only certain access allowed for country
    if (this.isCountry() && !this.isCountryPermissionAllowed(moduleCode, checkPerm)) return false;

    return true;
  }

  async canAccessRoute(route: string, moduleCode: string, moduleKey?: string, permission?: IamAction) {
    if (!route) return false;

    const canAccessRoutes = ['dash_dash', 'acc_settings', 'noti', 'global-search', 'page-not-found', 'permission-denied'];

    if (moduleCode && canAccessRoutes.includes(moduleCode)) return true;

    if (this.userPermsCached.length === 0) await this.setUserPermsCache();

    const allowed = this.userPermsCached.find((item) => item.moduleCode === moduleCode);
    if (allowed) {
      // If a specific permission is provided, check if the user has that permission
      // to block users from accessing a page by pasting the url directly.
      // If no specific permission is provided, check for READ permission.
      const checkPerm = permission ?? IamAction.READ;

      // To check the user's actual permission
      const hasPermission = (allowed.action & checkPerm) === checkPerm;

      // if user has permission and country view allows this permission, return true
      if (
        hasPermission &&
        (!this.isCountry() || this.isCountryPermissionAllowed(moduleCode, checkPerm))
      ) return true;
    } else {
      const isSubNavigation = /^\/?sub-navigation\/[^/]+$/.test(route);
      if (isSubNavigation) {
        const allowedSubNav = this.userPermsCached.find((item) => item.route === moduleKey);
        if (allowedSubNav) return true;
      }
    }

    return false;
  }

  // check if permissions are allowed in country view
  isCountryPermissionAllowed(moduleCode: string, checkPerm: IamAction) {
    const isAllowedModule = this.countryAllowedCRUDModules.includes(moduleCode);
    const isAllowedPermission = this.countryAllowedPermissions.includes(checkPerm);

    return isAllowedModule || isAllowedPermission; //if either one is true, return true
  }

  getTypeOfPermissionBasedOnLabel(label: string) {
    const labelLowercase = label.toLowerCase();

    const addCondition = labelLowercase === 'add' || labelLowercase === 'onboard_new_entity';
    const deleteCondition = labelLowercase === 'delete' || labelLowercase === 'delete-icon';
    const importCondition = labelLowercase === 'import';
    const exportCondition = labelLowercase === 'export' || labelLowercase === 'export_all';
    const editCondition =
      labelLowercase === 'edit' ||
      labelLowercase === 'edit-icon' ||
      labelLowercase === 'suspend' ||
      labelLowercase === 'add_user' ||
      labelLowercase === 'deactivate' ||
      labelLowercase === 'activate' ||
      labelLowercase === 'add_license_qty' ||
      labelLowercase === 'add_concurrent_seat';

    if (addCondition) return IamActionValues['CREATE'];
    if (deleteCondition) return IamActionValues['DELETE'];
    if (importCondition) return IamActionValues['IMPORT'];
    if (exportCondition) return IamActionValues['EXPORT'];
    if (editCondition) return IamActionValues['EDIT'];
  }
}
